/**
 * «Вставить систему» / «Заменить систему» — диалог AK-773 (Р35).
 *
 * Сценарий: агроном открыл техкарту, видит блок «Системы», жмёт «Вставить
 * систему» → выбирает из библиотеки → ДО сохранения получает ответ «что станет
 * с картой»: было / станет / дельта на гектар. Ровно тот же предпросмотр, что
 * у замены блока техкарты, — и это намеренно один язык: агроном уже умеет его
 * читать.
 *
 * Повторная вставка той же системы в ту же карту запрещена бэком (409). Экран
 * показывает СЛОВА сервера и рядом предлагает то единственное действие, которое
 * имеет смысл: заменить уже стоящую систему. Красный тост «409» здесь оставлял
 * бы человека без выхода.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import {
  formatDeltaPerHa,
  formatPerHa,
  techCardErrorText,
  type CardCost,
} from '@/lib/planning/techCards';
import {
  MODULE_KIND_SHORT,
  formatUsage,
  isAlreadyInsertedError,
  useInsertCardModule,
  useInsertModulePreview,
  useReplaceCardModule,
  useReplaceModulePreview,
  useSystemModules,
  type CardModuleRow,
  type ModuleApplyResult,
  type SystemModule,
} from '@/lib/planning/techCardModules';

export type PickerMode =
  | { kind: 'insert' }
  /** Замена: какую систему вынимаем из карты. */
  | { kind: 'replace'; current: CardModuleRow };

export function SystemPickerDialog({
  cardId,
  seasonId,
  cropId,
  mode: initialMode,
  inserted,
  onClose,
}: {
  cardId: string;
  seasonId: string | null;
  cropId: string | null;
  mode: PickerMode;
  /** Что уже стоит в карте — источник предложения «заменить» на 409. */
  inserted: CardModuleRow[];
  onClose: () => void;
}) {
  const [mode, setMode] = useState<PickerMode>(initialMode);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /** Система, уже стоящая в карте, на которую предлагаем перейти к замене. */
  const [conflict, setConflict] = useState<CardModuleRow | null>(null);

  const library = useSystemModules({ seasonId, cropId: null });
  // Фильтровать по культуре карты НЕЛЬЗЯ: система обработки почвы заводится
  // общей (`crop_id` пустой) и по фильтру исчезла бы совсем. Поэтому системы
  // «своей» культуры и общие идут первыми, чужие — ниже, но остаются видны.
  const items = (library.data ?? [])
    .filter((m) => m.status !== 'archived')
    .sort((a, b) => matchRank(a.crop_id, cropId) - matchRank(b.crop_id, cropId));

  const replacing = mode.kind === 'replace' ? mode.current : null;
  const insertPreview = useInsertModulePreview(
    mode.kind === 'insert' ? cardId : null,
    mode.kind === 'insert' ? pickedId : null,
  );
  const replacePreview = useReplaceModulePreview(
    replacing ? cardId : null,
    replacing?.module_id ?? null,
    replacing ? pickedId : null,
  );
  const preview = replacing ? replacePreview : insertPreview;

  const insert = useInsertCardModule(cardId);
  const replace = useReplaceCardModule(cardId);
  const applying = insert.isPending || replace.isPending;

  const apply = () => {
    if (!pickedId) return;
    setFailure(null);
    setConflict(null);
    const onError = (err: unknown) => {
      setFailure(techCardErrorText(err));
      // Предлагать замену можно ТОЛЬКО когда сервер сказал «уже стоит» (409).
      // На 500 или обрыве сети мы не знаем, что в карте, и кнопка «Заменить её»
      // звала бы заменить неизвестно что вместо честного «не сохранилось».
      if (!isAlreadyInsertedError(err)) {
        setConflict(null);
        return;
      }
      // Уже вставленная система того же типа — единственное, чем человек может
      // ответить на отказ. Ищем её здесь, а не гадаем в тексте.
      const picked = items.find((m) => m.id === pickedId);
      const standing = inserted.find(
        (row) => row.module_id === pickedId || (picked && row.kind === picked.kind),
      );
      setConflict(standing ?? null);
    };
    if (replacing) {
      replace.mutate(
        { oldModuleId: replacing.module_id, newModuleId: pickedId },
        { onSuccess: onClose, onError },
      );
    } else {
      insert.mutate({ moduleId: pickedId }, { onSuccess: onClose, onError });
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {replacing ? `Заменить систему «${replacing.name}»` : 'Вставить систему'}
          </DialogTitle>
          <DialogDescription>
            {replacing
              ? 'Старая система уйдёт из карты целиком, новая встанет на её место. Себестоимость пересчитается.'
              : 'Система встанет в карту целиком — со всеми обработками, препаратами и нормами. Себестоимость пересчитается.'}
          </DialogDescription>
        </DialogHeader>

        {library.isLoading && <Skeleton className="h-40 w-full" />}

        {!library.isLoading && items.length === 0 && (
          <p className="text-sm text-muted-foreground">
            В библиотеке сезона пока нет ни одной системы. Соберите её в разделе «Библиотека
            систем» — там же задаются обработки и препараты.
          </p>
        )}

        {items.length > 0 && (
          <div className="grid max-h-64 gap-2 overflow-y-auto sm:grid-cols-2">
            {items.map((m) => (
              <SystemOption
                key={m.id}
                module={m}
                picked={m.id === pickedId}
                onPick={() => {
                  setPickedId(m.id);
                  setFailure(null);
                  setConflict(null);
                }}
              />
            ))}
          </div>
        )}

        {pickedId && preview.isLoading && <Skeleton className="h-32 w-full" />}
        {pickedId && preview.isError && (
          <p className="text-sm text-destructive">{techCardErrorText(preview.error)}</p>
        )}
        {pickedId && preview.data && <PreviewTable result={preview.data} />}

        {failure && (
          <div className="rounded-md border border-border bg-muted p-3">
            <p role="alert" className="text-sm text-destructive">
              {failure}
            </p>
            {conflict && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  В карте уже стоит «{conflict.name}».
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setMode({ kind: 'replace', current: conflict });
                    setFailure(null);
                    setConflict(null);
                  }}
                >
                  Заменить её
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={applying}>
            Отмена
          </Button>
          <Button onClick={apply} disabled={!pickedId || applying}>
            {replacing ? 'Заменить систему' : 'Вставить систему'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SystemOption({
  module,
  picked,
  onPick,
}: {
  module: SystemModule;
  picked: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={picked}
      onClick={onPick}
      className={
        picked
          ? 'rounded-md border border-primary bg-primary/10 p-3 text-left transition-colors'
          : 'rounded-md border border-border bg-background p-3 text-left transition-colors hover:bg-accent'
      }
    >
      <div className="text-sm font-medium text-foreground">{module.name}</div>
      <div className="text-xs text-muted-foreground">
        {MODULE_KIND_SHORT[module.kind] ?? 'Система'} · {module.operations_count} обработок ·{' '}
        {formatUsage(module.used_in_cards_count)}
      </div>
      <div className="mt-2 text-base font-semibold text-foreground">
        {formatPerHa(module.cost?.per_ha ?? null)}
      </div>
    </button>
  );
}

/** «Было → станет → дельта» — тот же вид, что у замены блока техкарты. */
function PreviewTable({ result }: { result: ModuleApplyResult }) {
  const rows = [
    {
      label: 'Прямые затраты карты',
      was: formatPerHa(result.before?.per_ha ?? null),
      now: formatPerHa(result.after?.per_ha ?? null),
      delta: formatDeltaPerHa(result.delta_per_ha),
      worse: result.delta_per_ha > 0,
    },
    {
      label: 'Операций в карте',
      was: String(countOps(result.before)),
      now: String(countOps(result.after)),
      delta:
        countOps(result.after) === countOps(result.before)
          ? 'столько же'
          : `${countOps(result.after) > countOps(result.before) ? '+' : '−'}${Math.abs(
              countOps(result.after) - countOps(result.before),
            )}`,
      worse: false,
    },
  ];
  return (
    <div className="rounded-md border border-border bg-background p-4">
      <div className="text-sm font-semibold text-foreground">
        Что станет с техкартой, если применить
      </div>
      <Table className="mt-2">
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell className="py-2 text-muted-foreground">{row.label}</TableCell>
              <TableCell className="py-2 text-muted-foreground line-through">{row.was}</TableCell>
              <TableCell className="py-2 font-medium text-foreground">{row.now}</TableCell>
              <TableCell
                className={
                  row.worse ? 'py-2 text-right text-destructive' : 'py-2 text-right text-primary'
                }
              >
                {row.delta}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** 0 — культура карты, 1 — общая система, 2 — чужая культура. */
function matchRank(moduleCropId: string | null, cardCropId: string | null): number {
  if (moduleCropId && cardCropId && moduleCropId === cardCropId) return 0;
  if (!moduleCropId) return 1;
  return 2;
}

function countOps(cost: CardCost | null | undefined): number {
  return cost?.operations?.length ?? 0;
}
